import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, Tag, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

type Acc = {
  id: string; nganHang: string; bankName: string; so: string; chuTk: string; bietDanh?: string; uuTien?: number;
  hanMucNgay?: number; soLenhNgay?: number; ngungLuc?: string; state: 'active' | 'off' | 'retired';
  usedToday: string; ordersToday: number; openOrders: number; full: boolean;
};
type Data = { items: Acc[]; version: number; sepay: boolean };

const STATE: Record<Acc['state'], [string, 'green' | 'gray' | 'red']> = { active: ['Đang dùng', 'green'], off: ['Tắt', 'gray'], retired: ['Đã ngừng', 'red'] };

function Cap({ used, cap, money }: { used: number; cap?: number; money?: boolean }) {
  const v = money ? <Money v={String(used)} /> : used;
  if (!cap) return <>{v} <span className="muted">/ không giới hạn</span></>;
  const over = used >= cap;
  return <span className={over ? 'text-error' : undefined}>{v} / {money ? <Money v={String(cap)} /> : cap}</span>;
}

// The receiving-account pool for today (Korea day, the same count he A uses for the caps): what
// each account took, against its amount and order caps, and one click to retire an account a bank
// froze (its open unclaimed orders close; players get another account on their next order).
export function WalletAccountsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['wallet-accounts'], queryFn: () => api.get<Data>('/api/admin/wallet/accounts'), refetchInterval: 30_000 });
  const [pick, setPick] = useState<Acc | null>(null);
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string } | null>(null);
  const retire = useMutation({
    mutationFn: () => api.post<{ command: Command }>(`/api/admin/wallet/accounts/${pick!.id}/retire`, { retire: pick!.state !== 'retired', note: note.trim(), expect: q.data!.version }),
    onSuccess: (r) => {
      const c = r.command;
      setNotice(c.status === 'SUCCEEDED'
        ? { kind: 'success', title: pick!.state === 'retired' ? `Đã dùng lại tài khoản ${pick!.so}` : `Đã ngừng tài khoản ${pick!.so}. Lệnh chưa chuyển trên tài khoản này đã đóng.` }
        : { kind: 'warning', title: (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status });
      setPick(null); setNote('');
      qc.invalidateQueries({ queryKey: ['wallet-accounts'] }); qc.invalidateQueries({ queryKey: ['wallet-config'] });
    },
  });
  const columns: ColumnDef<Acc, any>[] = [
    { header: 'Tài khoản', id: 'tk', cell: (c) => <><strong>{c.row.original.bankName}</strong> · {c.row.original.so}<br /><span className="muted">{c.row.original.chuTk}{c.row.original.bietDanh ? ' · ' + c.row.original.bietDanh : ''}</span></> },
    { header: 'Trạng thái', accessorKey: 'state', cell: (c) => <>
      <Tag size="sm" type={STATE[c.row.original.state][1]}>{STATE[c.row.original.state][0]}</Tag>
      {c.row.original.full && c.row.original.state === 'active' ? <Tag size="sm" type="warm-gray">Đủ hạn mức hôm nay</Tag> : null}
      {c.row.original.ngungLuc ? <div className="muted"><When v={c.row.original.ngungLuc} /></div> : null}</> },
    { header: 'Ưu tiên', accessorKey: 'uuTien', cell: (c) => c.getValue() ?? 0 },
    { header: 'Tiền hôm nay', id: 'tien', cell: (c) => <Cap used={Number(c.row.original.usedToday)} cap={c.row.original.hanMucNgay} money /> },
    { header: 'Lệnh hôm nay', id: 'lenh', cell: (c) => <Cap used={c.row.original.ordersToday} cap={c.row.original.soLenhNgay} /> },
    { header: 'Lệnh đang mở', accessorKey: 'openOrders' },
    { header: '', id: 'act', cell: (c) => (can('wallet.config')
      ? <Button kind={c.row.original.state === 'retired' ? 'ghost' : 'danger--ghost'} size="sm" onClick={(e) => { e.stopPropagation(); setPick(c.row.original); }}>
        {c.row.original.state === 'retired' ? 'Dùng lại' : 'Ngừng dùng'}</Button> : null) },
  ];
  return (
    <div className="page">
      <PageHeader title="Tài khoản nhận hôm nay" lead="Mỗi lệnh nạp nhận một tài khoản: ưu tiên nhỏ trước, cùng ưu tiên thì tài khoản nhận ít tiền nhất hôm nay trước; tài khoản đã đủ hạn mức tiền hoặc số lệnh trong ngày (giờ Hàn) bị bỏ qua. Ngân hàng khóa tài khoản thì bấm Ngừng dùng: tài khoản không được cấp nữa, lệnh chưa chuyển trên đó đóng lại, tiền về sau vẫn vào hàng giao dịch chưa khớp." />
      <p className="muted">Webhook SePay: {q.data ? (q.data.sepay ? 'đang bật' : 'đang tắt') : '…'}. Hạn mức, ưu tiên và bật/tắt sửa ở <Link to="/wallet/config">cấu hình nạp / rút</Link>.</p>
      {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} onClose={() => setNotice(null)} /> : null}
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()} rowKey={(r) => r.id}
        empty={{ title: 'Chưa có tài khoản nhận', body: 'Thêm tài khoản ở cấu hình nạp / rút.' }} />
      <Modal open={!!pick} danger={pick?.state !== 'retired'}
        modalHeading={pick ? `${pick.state === 'retired' ? 'Dùng lại' : 'Ngừng dùng'} ${pick.bankName} ${pick.so}?` : ''}
        primaryButtonText={pick?.state === 'retired' ? 'Dùng lại' : 'Ngừng dùng'} secondaryButtonText="Hủy"
        primaryButtonDisabled={note.trim().length < 3 || retire.isPending}
        onRequestClose={() => { setPick(null); setNote(''); retire.reset(); }} onRequestSubmit={() => retire.mutate()}>
        <p>{pick?.state === 'retired'
          ? 'Tài khoản được cấp lại cho lệnh nạp mới.'
          : `Không cấp tài khoản này cho lệnh mới. ${pick?.openOrders ?? 0} lệnh đang mở: lệnh chưa báo đã chuyển sẽ đóng; lệnh người chơi đã báo chuyển vẫn chờ duyệt.`}</p>
        <TextInput id="acc-note" labelText="Lý do (bắt buộc)" value={note} onChange={(e) => setNote(e.target.value)} />
        <InlineError error={retire.error} />
      </Modal>
    </div>
  );
}
